import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { CardHeader, CardContent, Grid, TextField } from '@material-ui/core';
import styles from 'styles/admin-detail.module.scss';
import {
  CONTRACT_TYPE_VALUES,
  INSURANCE_LINE_VALUES,
  IDENTIFIED_STATE,
  CANCELLATION_CATEGORY,
} from 'const';
import { ErrorMessage } from 'components';
import { currentUserSelector } from "store/current_user"
import { convertToJapaneseCalendar } from 'utils/japaneseCalendarConverter';
import { isPermitted } from 'utils/utils';
import Accordion from "@material-ui/core/ExpansionPanel";
import AccordionSummary from "@material-ui/core/ExpansionPanelSummary";
import ExpandMoreIcon from '@material-ui/icons/ExpandMoreOutlined';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import { getStylesBackgroundColor} from 'views/Detail/components';
import { getStylesLabelColorLine} from 'views/Detail/components';
import { getIdentifiesStateLabel, getFeeCategoryLabel } from 'utils/utils';

const PersonalInformation = (props) => {
  const {
    personalInfo,
    insuranceType,
    feeCategory,
    cancellationCategory,
    basicInformationInputFormik,
    lockedBy
  } = props;
  const currentUser = useSelector(currentUserSelector);
  const getIdentifiedLabel = (identifiedResults, key) => {
    const identifiedValue = identifiedResults?.[key] || IDENTIFIED_STATE.UNIDENTIFIED
    return (
      <span
        className={`${styles.IdentifiedLabelArea} ${getIdentifiedLabelColor(identifiedValue)}`}
      >
        {getIdentifiesStateLabel(identifiedValue)}
      </span>
    )
  };

  const getIdentifiedLabelColor = (identifiedValue) => {
    if (identifiedValue === IDENTIFIED_STATE.OK.VALUE) {
      return styles.identifiedOkColor
    }
    if (identifiedValue === IDENTIFIED_STATE.NG.VALUE) {
      return styles.identifiedNgColor
    }
    return styles.identifiedUnidentifiedColor
  }

  // 未突合ステータスで画面にアクセスした時にOK/NGにしてもプルダウンから未突合が消えないようにuseStateで値を維持する
  const [isIdentifiedAllBeforeChange] = useState(basicInformationInputFormik.values.isIdentifiedAll)

    return (
    <>
      <CardHeader
        title='本人確認情報'
        className={getStylesBackgroundColor(insuranceType)}
      />
      <CardContent className={styles.cardContent}>
        <Grid container spacing={5}>
          <Grid item xs={2}>
            <div className={styles.listItem}>
              <p className={getStylesLabelColorLine(insuranceType)}>本人確認</p>
              <Select
                  fullWidth
                  className={styles.selecter}
                  {...basicInformationInputFormik.getFieldProps('isIdentifiedAll')}
                  disabled={!isPermitted(currentUser, lockedBy)}
                  onChange={(event) => {
                    basicInformationInputFormik.setFieldValue(
                      'isIdentifiedAll',
                      event.target.value
                    );
                  }}
                  value={basicInformationInputFormik.values.isIdentifiedAll || ""}
                  error={
                    !!(
                      basicInformationInputFormik.touched.isIdentifiedAll &&
                      basicInformationInputFormik.errors.isIdentifiedAll
                    )
                  }
                >
                  { isIdentifiedAllBeforeChange === IDENTIFIED_STATE.UNIDENTIFIED.VALUE &&
                    <MenuItem value={IDENTIFIED_STATE.UNIDENTIFIED.VALUE}>{IDENTIFIED_STATE.UNIDENTIFIED.LABEL}</MenuItem>
                  }
                  <MenuItem value={IDENTIFIED_STATE.OK.VALUE}>{IDENTIFIED_STATE.OK.LABEL}</MenuItem>
                  <MenuItem value={IDENTIFIED_STATE.NG.VALUE}><span className={styles.identifiedNgText}>{IDENTIFIED_STATE.NG.LABEL}</span></MenuItem>
                </Select>
            </div>
          </Grid>
          <Grid item xs={2}>
            <div className={styles.listItem}>
              <p className={getStylesLabelColorLine(insuranceType)}>フィー区分</p>
              <p className={styles.text}>
                {getFeeCategoryLabel(feeCategory) || '-'}
              </p>
            </div>
          </Grid>
          <Grid item xs={2}>
            <div className={styles.listItem}>
              <p className={getStylesLabelColorLine(insuranceType)}>解約区分</p>
              <p className={styles.text}>
                {CANCELLATION_CATEGORY.find(({_, value}) => value === cancellationCategory)?.label || '-'}
              </p>
            </div>
          </Grid>

          <Grid item xs={2}>
            <div className={styles.listItem}>
              <p className={getStylesLabelColorLine(insuranceType)}>保険種目</p>
              <p className={styles.text}>
                {insuranceType ? INSURANCE_LINE_VALUES[insuranceType] : '-'}
              </p>
            </div>
          </Grid>
          <Grid item xs={2}>
            <div className={styles.listItem}>
              <p className={getStylesLabelColorLine(insuranceType)}>個人法人区分</p>
              <p className={styles.text}>
                {personalInfo?.isPersonal === 1
                  ? '個人'
                  : personalInfo?.isPersonal === 2
                  ? '法人'
                  : '-'}
              </p>
            </div>
          </Grid>
          <Grid item xs={2}>
            <div className={styles.listItem}>
              <p className={getStylesLabelColorLine(insuranceType)}>入力者</p>
              <p className={styles.text}>
                {personalInfo?.inputter
                  ? CONTRACT_TYPE_VALUES[personalInfo?.inputter]
                  : '-'}
              </p>
            </div>
          </Grid>
        </Grid>
        <Grid container spacing={5}>
          <Grid item xs={3}>
            <div className={styles.listItem}>
              <p className={getStylesLabelColorLine(insuranceType)}>証券番号（お客さま入力）
                {getIdentifiedLabel(personalInfo, 'isIdentifiedSyoken')}
              </p>
              <p className={styles.text}>{personalInfo?.policyNumber || '-'}</p>
            </div>
          </Grid>
          <Grid item xs={3}>
            <div className={styles.listItem}>
              <p className={getStylesLabelColorLine(insuranceType)}>明細番号（お客さま入力）</p>
              <p className={styles.text}>{personalInfo?.detailNumber || '-'}</p>
            </div>
          </Grid>
          <Grid item xs={3}>
            <div className={styles.listItem}>
              <p className={getStylesLabelColorLine(insuranceType)}>契約者名（お客さま入力・漢字）</p>
              <p className={styles.text}>{personalInfo?.name || '-'}</p>
            </div>
          </Grid>
          <Grid item xs={3}>
            <div className={styles.listItem}>
              <p className={getStylesLabelColorLine(insuranceType)}>契約者名（お客さま入力・カナ）
                {getIdentifiedLabel(personalInfo, 'isIdentifiedNameKana')}
              </p>
              <p className={styles.text}>{personalInfo?.nameKana || '-'}</p>
            </div>
          </Grid>
        </Grid>
        <Grid container spacing={5}>
          <Grid item xs={3}>
            <div className={styles.listItem}>
              <p className={getStylesLabelColorLine(insuranceType)}>証券番号（修正後）</p>
              <TextField
                fullWidth
                type={'text'}
                rows={1}
                variant='outlined'
                disabled={!isPermitted(currentUser, lockedBy)}
                {...basicInformationInputFormik.getFieldProps("policyNumberRevised")}
                error={
                  !!(
                    basicInformationInputFormik.touched.policyNumberRevised &&
                    basicInformationInputFormik.errors.policyNumberRevised
                  )
                }
              />
              <ErrorMessage
                touched={
                  basicInformationInputFormik.touched.policyNumberRevised
                }
                error={basicInformationInputFormik.errors.policyNumberRevised}
              />
            </div>
          </Grid>
          <Grid item xs={3}></Grid>
          <Grid item xs={3}>
            <div className={styles.listItem}>
              <p className={getStylesLabelColorLine(insuranceType)}>契約者名（修正後・漢字）</p>
              <TextField
                fullWidth
                type={'text'}
                rows={1}
                variant='outlined'
                disabled={!isPermitted(currentUser, lockedBy)}
                {...basicInformationInputFormik.getFieldProps("nameRevised")}
                error={
                  !!(
                    basicInformationInputFormik.touched.nameRevised &&
                    basicInformationInputFormik.errors.nameRevised
                  )
                }
              />
              <ErrorMessage
                touched={basicInformationInputFormik.touched.nameRevised}
                error={basicInformationInputFormik.errors.nameRevised}
              />
            </div>
          </Grid>
          <Grid item xs={3}>
            <div className={styles.listItem}>
              <p className={getStylesLabelColorLine(insuranceType)}>契約者名（修正後・カナ）</p>
              <TextField
                fullWidth
                type={'text'}
                rows={1}
                variant='outlined'
                disabled={!isPermitted(currentUser, lockedBy)}
                {...basicInformationInputFormik.getFieldProps("nameKanaRevised")}
                error={
                  !!(
                    basicInformationInputFormik.touched.nameKanaRevised &&
                    basicInformationInputFormik.errors.nameKanaRevised
                  )
                }
              />
              <ErrorMessage
                touched={basicInformationInputFormik.touched.nameKanaRevised}
                error={basicInformationInputFormik.errors.nameKanaRevised}
              />
            </div>
          </Grid>
        </Grid>
        <Grid container spacing={5}>
          <Grid item xs={3}>
            <div className={styles.listItem}>
              <p className={getStylesLabelColorLine(insuranceType)}>ご契約の電話番号
                {getIdentifiedLabel(personalInfo, 'isIdentifiedTel')}
              </p>
              <p className={styles.text}>
                {personalInfo?.registeredTel1 &&
                personalInfo?.registeredTel2 &&
                personalInfo?.registeredTel3
                  ? personalInfo?.registeredTel1 +
                    '-' +
                    personalInfo?.registeredTel2 +
                    '-' +
                    personalInfo?.registeredTel3
                  : '-'}
              </p>
            </div>
          </Grid>
          <Grid item xs={3}>
            <div className={styles.listItem}>
              <p className={getStylesLabelColorLine(insuranceType)}>現在ご契約のお車の登録番号</p>
              <p className={styles.text}>{personalInfo?.carNumber || '-'}</p>
            </div>
          </Grid>
          <Grid item xs={3}>
            <div className={styles.listItem}>
              <p className={getStylesLabelColorLine(insuranceType)}>契約者の生年月日</p>
              <p className={styles.text}>
                {convertToJapaneseCalendar(personalInfo?.birthday) || '-'}
              </p>
            </div>
          </Grid>
        </Grid>
        <Grid container spacing={5}>
          <Grid item xs={3}>
            <div className={`${styles.listItem} ${styles.item}`}>
              <p className={getStylesLabelColorLine(insuranceType)}>現在ご登録の郵便番号</p>
              <p className={styles.text}>{personalInfo?.zipCode || '-'}</p>
            </div>
          </Grid>
          <Grid item xs={3}>
            <div className={`${styles.listItem} ${styles.item}`}>
              <p className={getStylesLabelColorLine(insuranceType)}>現在ご登録の住所</p>
              <p className={styles.text}>
                {(personalInfo?.prefecture || '') +
                  (personalInfo?.city || '') +
                  (personalInfo?.chome || '') +
                  '　' +
                  (personalInfo?.building || '') || '-'}
              </p>
            </div>
          </Grid>
          <Grid item xs={3}>
            <div className={`${styles.listItem} ${styles.item}`}>
              <p className={getStylesLabelColorLine(insuranceType)}>携帯電話番号 （日中連絡先）</p>
              <p className={styles.text}>{personalInfo?.contactTel || '-'}</p>
            </div>
          </Grid>
          <Grid item xs={3}>
            <div className={`${styles.listItem} ${styles.item}`}>
              <p className={getStylesLabelColorLine(insuranceType)}>メールアドレス</p>
              <p className={styles.text}>{personalInfo?.email || '-'}</p>
            </div>
          </Grid>
        </Grid>
        <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          className={getStylesBackgroundColor(insuranceType)}
        >
          部店担当店・代理店情報
        </AccordionSummary>
          <CardContent className={styles.cardContent}>
            <Grid container spacing={5}>
              <Grid item xs={3}>
                <div className={styles.listItem}>
                  <p className={getStylesLabelColorLine(insuranceType)}>部担コード</p>
                  <p className={styles.text}>{personalInfo?.deptCode || '-'}</p>
                </div>
              </Grid>
              <Grid item xs={3}>
                <div className={styles.listItem}>
                  <p className={getStylesLabelColorLine(insuranceType)}>部担名</p>
                  <p className={styles.text}>{personalInfo?.deptName || '-'}</p>
                </div>
              </Grid>
              <Grid item xs={3}>
                <div className={styles.listItem}>
                  <p className={getStylesLabelColorLine(insuranceType)}>代理店コード</p>
                  <p className={styles.text}>{personalInfo?.agencyCode || '-'}</p>
                </div>
              </Grid>
              <Grid item xs={3}>
                <div className={styles.listItem}>
                  <p className={getStylesLabelColorLine(insuranceType)}>代理店名</p>
                  <p className={styles.text}>{personalInfo?.agencyName || '-'}</p>
                </div>
              </Grid>
            </Grid>
          </CardContent>
        </Accordion>
      </CardContent>
    </>
  );
};

export default PersonalInformation;
